import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { Button, HStack, Skeleton, Stack, Text } from '@chakra-ui/react'
import { LuArrowLeft } from 'react-icons/lu'
import { PageHeader } from '../components/ui/PageHeader'
import { toaster } from '../components/ui/toaster'
import { UserBadges } from '../components/users/UserBadges'
import { AccountCard } from '../components/users/AccountCard'
import { PlatformRolesCard } from '../components/users/PlatformRolesCard'
import { EventRolesCard } from '../components/users/EventRolesCard'
import { ContentCard } from '../components/users/ContentCard'
import { DangerZoneCard } from '../components/users/DangerZoneCard'
import { getAuthUser } from '../services/userAdmin'
import { useAuth } from '../contexts/AuthContext'
import { accountLabel } from '../utils/userDisplay'
import { useTranslation } from '../i18n'

export default function UserDetailPage() {
    const { uid } = useParams()
    const navigate = useNavigate()
    const { user: currentUser } = useAuth()
    const { t } = useTranslation()
    const [account, setAccount] = useState(null)
    const [missing, setMissing] = useState(false)

    const reload = useCallback(() => getAuthUser({ uid }).then(
        (data) => {
            setAccount(data)
            setMissing(false)
        },
        (err) => {
            if (err.code === 'functions/not-found') setMissing(true)
            else toaster.create({ title: t('users.loadFailed', { message: err.message }), type: 'error' })
        },
    ), [uid, t])

    useEffect(() => {
        reload()
    }, [reload])

    // Every change on this page: call, say so, re-read the account. Resolves to
    // whether it worked, so a form can decide whether to close.
    const act = useCallback(async (fn, success) => {
        try {
            await fn()
            if (success) toaster.create({ title: success, type: 'success' })
            await reload()
            return true
        } catch (err) {
            toaster.create({ title: err.message, type: 'error' })
            return false
        }
    }, [reload])

    const back = (
        <Button asChild size="sm" variant="outline">
            <RouterLink to="/users"><LuArrowLeft /> {t('users.back')}</RouterLink>
        </Button>
    )

    if (missing) {
        return (
            <Stack gap={4}>
                <PageHeader title={t('users.title')}>{back}</PageHeader>
                <Text color="var(--mc-text-muted)">{t('users.notFound')}</Text>
            </Stack>
        )
    }

    // Also while moving from one person to another: never show the last one's
    // controls under the new one's URL.
    if (!account || account.uid !== uid) {
        return (
            <Stack gap={4}>
                <PageHeader title={t('users.title')}>{back}</PageHeader>
                {[1, 2, 3].map((i) => <Skeleton key={i} height="140px" borderRadius="lg" />)}
            </Stack>
        )
    }

    const isSelf = currentUser?.uid === account.uid

    // The account is gone after a delete, so the list is the only place left;
    // after removing access the person still exists, so stay and re-read.
    const onRemoved = (mode) => {
        if (mode === 'access') {
            toaster.create({ title: t('users.delete.doneAccess'), type: 'success' })
            reload()
        } else {
            toaster.create({ title: t('users.delete.doneAccount'), type: 'success' })
            navigate('/users')
        }
    }

    return (
        <Stack gap={8} maxW="900px">
            <Stack gap={2}>
                <PageHeader title={accountLabel(account, t)}>{back}</PageHeader>
                <HStack gap={2} mt={-4}>
                    {isSelf && <Text fontSize="sm" color="var(--mc-text-muted)">{t('users.you')}</Text>}
                    <UserBadges user={account} />
                </HStack>
            </Stack>
            <AccountCard user={account} isSelf={isSelf} act={act} />
            <PlatformRolesCard user={account} isSelf={isSelf} act={act} />
            <EventRolesCard user={account} act={act} />
            <ContentCard user={account} />
            <DangerZoneCard user={account} onDone={onRemoved} />
        </Stack>
    )
}
